import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  FormControlLabel,
  Switch,
  Typography,
  CircularProgress,
  LinearProgress,
  InputAdornment,
} from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import {
  useCreateCourseAdMutation,
  useUpdateCourseAdMutation,
  useGetAdminCourseAdsQuery,
} from "@/store/api/homepageApi";
import FileUploadButton from "@/components/common/FileUploadButton";
import { RichTextEditor } from "@/components/common";
import { uploadMediaFiles, uploadToCloudinary } from "@/utils/cloudinaryUpload";

const validationSchema = Yup.object({
  title: Yup.string()
    .required("Title is required")
    .max(200, "Title must be less than 200 characters"),
  teacherImage: Yup.string().url("Must be a valid URL").nullable(),
  coverImage: Yup.string().url("Must be a valid URL").nullable(),
  description: Yup.string().max(1000, "Description must be less than 1000 characters"),
  sampleVideoUrl: Yup.string().url("Must be a valid URL").nullable(),
  displayOrder: Yup.number().min(0, "Display order must be 0 or greater"),
  price: Yup.number()
    .min(0, "Price must be a non-negative number")
    .nullable()
    .transform((value, original) => (original === "" ? null : value)),
});

const CourseAdForm = ({ open, onClose, courseAd, onSuccess }) => {
  const [createCourseAd, { isLoading: isCreating }] = useCreateCourseAdMutation();
  const [updateCourseAd, { isLoading: isUpdating }] = useUpdateCourseAdMutation();
  const { data: courseAdsData } = useGetAdminCourseAdsQuery({});

  const isEditMode = Boolean(courseAd);
  const isLoading = isCreating || isUpdating;
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);

  // Calculate next display order for new course ads
  const getNextDisplayOrder = () => {
    if (!courseAdsData?.data || courseAdsData.data.length === 0) {
      return 0;
    }
    const maxOrder = Math.max(...courseAdsData.data.map(ad => ad.displayOrder || 0));
    return maxOrder + 1;
  };

  const formik = useFormik({
    initialValues: {
      title: courseAd?.title || "",
      teacherImage: courseAd?.teacherImage || "",
      teacherImageFile: null,
      coverImage: courseAd?.coverImage || "",
      coverImageFile: null,
      description: courseAd?.description || "",
      courseContent: courseAd?.courseContent || "",
      sampleVideoUrl: courseAd?.sampleVideoUrl || "",
      isActive: courseAd?.isActive ?? true,
      displayOrder: courseAd?.displayOrder ?? (isEditMode ? 0 : getNextDisplayOrder()),
      price: courseAd?.price ?? "",
    },
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      try {
        // Upload files first with progress tracking
        const mediaFiles = {};
        if (values.teacherImageFile) {
          mediaFiles.teacherImage = values.teacherImageFile;
        }
        if (values.coverImageFile) {
          mediaFiles.coverImage = values.coverImageFile;
        }

        let uploadedUrls = {};
        if (Object.keys(mediaFiles).length > 0) {
          setIsUploading(true);
          setUploadProgress(0);
          
          // Custom upload with progress tracking
          const uploads = [];
          
          if (mediaFiles.teacherImage) {
            uploads.push(
              uploadToCloudinary(
                mediaFiles.teacherImage, 
                `toeic/homepage/course-covers/${process.env.NODE_ENV === 'development' ? 'dev/' : ''}images`,
                (progress) => setUploadProgress(progress)
              ).then(result => ({ type: 'teacherImage', url: result.secure_url }))
            );
          }
          
          if (mediaFiles.coverImage) {
            uploads.push(
              uploadToCloudinary(
                mediaFiles.coverImage, 
                `toeic/homepage/course-covers/${process.env.NODE_ENV === 'development' ? 'dev/' : ''}images`,
                (progress) => setUploadProgress(progress)
              ).then(result => ({ type: 'coverImage', url: result.secure_url }))
            );
          }
          
          const results = await Promise.all(uploads);
          uploadedUrls = results.reduce((acc, { type, url }) => {
            acc[`${type}Url`] = url;
            return acc;
          }, {});
          
          setIsUploading(false);
          setUploadProgress(100);
        }

        const payload = {
          title: values.title,
          teacherImage: uploadedUrls.teacherImageUrl || values.teacherImage || undefined,
          coverImage: uploadedUrls.coverImageUrl || values.coverImage || undefined,
          description: values.description || undefined,
          courseContent: values.courseContent || undefined,
          sampleVideoUrl: values.sampleVideoUrl || undefined,
          isActive: values.isActive,
          displayOrder: values.displayOrder,
          price: values.price !== "" && values.price !== null && values.price !== undefined
            ? Number(values.price)
            : null,
        };

        if (isEditMode) {
          await updateCourseAd({ id: courseAd._id, ...payload }).unwrap();
          toast.success("Course ad updated successfully");
        } else {
          await createCourseAd(payload).unwrap();
          toast.success("Course ad created successfully");
        }
        
        onSuccess();
        handleClose();
      } catch (error) {
        setIsUploading(false);
        setUploadProgress(0);
        toast.error(error?.data?.message || "Failed to save course ad");
      }
    },
  });

  const handleClose = () => {
    formik.resetForm();
    onClose();
  };

  const handleTeacherImageFileChange = (data) => {
    const file = data?.file || data;
    if (file) {
      formik.setFieldValue("teacherImageFile", file);
      formik.setFieldValue("teacherImage", null); // Clear existing URL
    }
  };

  const handleCoverImageFileChange = (data) => {
    const file = data?.file || data;
    if (file) {
      formik.setFieldValue("coverImageFile", file);
      formik.setFieldValue("coverImage", null); // Clear existing URL
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {isEditMode ? "Edit Course Ad" : "Create Course Ad"}
      </DialogTitle>
      <form onSubmit={formik.handleSubmit}>
        {(isUploading || isLoading) && (
          <Box sx={{ width: '100%' }}>
            <LinearProgress 
              variant="determinate" 
              value={isUploading ? uploadProgress : 50} 
              sx={{ 
                height: 6,
                '& .MuiLinearProgress-bar': {
                  transition: 'transform 0.2s ease-in-out'
                }
              }} 
            />
          </Box>
        )}
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <TextField
              fullWidth
              label="Title *"
              name="title"
              value={formik.values.title}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.title && Boolean(formik.errors.title)}
              helperText={formik.touched.title && formik.errors.title}
            />


            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Book Image (Optional)
              </Typography>
              <FileUploadButton
                onFileSelect={handleCoverImageFileChange}
                accept="image/*"
                label={formik.values.coverImageFile?.name || "Upload Book Image"}
                currentFile={formik.values.coverImageFile || formik.values.coverImage}
                previewUrl={formik.values.coverImage}
                name="coverImage"
              />
            </Box>

            <TextField
              fullWidth
              label="Description"
              name="description"
              multiline
              rows={3}
              value={formik.values.description}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.description && Boolean(formik.errors.description)}
              helperText={formik.touched.description && formik.errors.description}
            />

            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Course Content (Optional)
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                Rich text content displayed when users click "Course Details" on the homepage
              </Typography>
              <RichTextEditor
                value={formik.values.courseContent}
                onChange={(content) => formik.setFieldValue("courseContent", content)}
                placeholder="Enter detailed course content..."
                minHeight="200px"
              />
            </Box>

            <TextField
              fullWidth
              label="Sample Video URL"
              name="sampleVideoUrl"
              value={formik.values.sampleVideoUrl}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.sampleVideoUrl && Boolean(formik.errors.sampleVideoUrl)}
              helperText={
                formik.touched.sampleVideoUrl && formik.errors.sampleVideoUrl
                  ? formik.errors.sampleVideoUrl
                  : "YouTube video URL"
              }
            />

            <TextField
              fullWidth
              label="Price"
              name="price"
              type="number"
              value={formik.values.price}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.price && Boolean(formik.errors.price)}
              helperText={
                formik.touched.price && formik.errors.price
                  ? formik.errors.price
                  : "Leave empty to hide price. Enter 0 for free."
              }
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">₫</InputAdornment>
                ),
                inputProps: { min: 0 },
              }}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={formik.values.isActive}
                  onChange={(e) => formik.setFieldValue("isActive", e.target.checked)}
                  name="isActive"
                />
              }
              label="Active"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isLoading}
            startIcon={isLoading && <CircularProgress size={20} />}
          >
            {isEditMode ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default CourseAdForm;
